import { Link } from "react-router-dom";
import { GraduationCap, Target, Zap, GitBranch, TrendingUp, ShieldCheck, ArrowRight, Building2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1749310726959-d8fccfef7ee4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwY29ycG9yYXRlJTIwb2ZmaWNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDkzODEzNHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Target, title: "Career Readiness Score", desc: "Transparent role-specific % scores computed against a 5-table Career Knowledge Graph." },
  { icon: TrendingUp, title: "CV Impact Engine", desc: "See the exact +% each hackathon, cert or project adds to every dream-career track." },
  { icon: Zap, title: "Daily Micro-Action", desc: "One highest-impact move a day. Earn XP, build streaks, level up your profile." },
  { icon: GitBranch, title: "Career Digital Twin", desc: "A living timeline of verified skills, certifications, projects and internships." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-[#6366F1] flex items-center justify-center">
            <GraduationCap size={20} className="text-white" />
          </div>
          <span className="font-extrabold tracking-tight text-lg">Enjyme</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="landing-login" className="text-sm text-slate-300 hover:text-white transition-colors px-4 py-2">Sign in</Link>
          <Link to="/register" data-testid="landing-get-started" className="text-sm font-semibold bg-[#6366F1] hover:bg-[#4F46E5] transition-colors px-5 py-2.5 rounded-lg">Get started</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20 grid lg:grid-cols-2 gap-14 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/70 border border-slate-700 mb-6">
            <span className="h-2 w-2 rounded-full bg-[#22C55E] animate-pulse" />
            <span className="text-xs font-mono text-slate-300">200+ careers · 1,100+ skills mapped</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Your Career,<br /><span className="text-[#6366F1]">measured</span> in real time.
          </h1>
          <p className="text-slate-400 text-base mt-6 max-w-lg leading-relaxed">
            Enjyme is the Career GPS for ambitious students. Track your evolving profile against
            role-specific readiness scores, and know the exact CV impact of everything you do next.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/register" data-testid="hero-cta" className="inline-flex items-center gap-2 font-semibold bg-[#6366F1] hover:bg-[#4F46E5] transition-colors px-6 py-3.5 rounded-lg">
              Build your profile <ArrowRight size={18} />
            </Link>
            <Link to="/register?role=recruiter" data-testid="hero-recruiter" className="inline-flex items-center gap-2 font-semibold bg-slate-800 hover:bg-slate-700 transition-colors px-6 py-3.5 rounded-lg border border-slate-700">
              <Building2 size={18} /> I'm a recruiter
            </Link>
          </div>
          <div className="flex items-center gap-2 mt-6 text-slate-500 text-sm">
            <ShieldCheck size={16} className="text-[#22C55E]" />
            Privacy-first · your profile is hidden from recruiters until you allow it.
          </div>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="absolute -inset-4 bg-[#6366F1]/20 blur-3xl rounded-full" />
          <img src={HERO} alt="Modern corporate workspace" className="relative rounded-2xl border border-slate-700 shadow-2xl object-cover w-full h-[440px]" />
          <div className="absolute -bottom-5 -left-5 bg-white text-slate-900 rounded-xl p-4 shadow-xl border-2 border-[#6366F1] w-56">
            <p className="eyebrow text-slate-500">TODAY'S IMPACT</p>
            <p className="font-mono text-2xl font-bold text-[#22C55E] mt-1">+4.8%</p>
            <p className="text-xs text-slate-500">Software Engineering readiness</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f) => (
            <div key={f.title} className="bg-white rounded-xl p-6 border border-slate-200 hover:-translate-y-1 transition-transform">
              <div className="h-11 w-11 rounded-lg bg-[#6366F1]/10 flex items-center justify-center mb-4">
                <f.icon size={22} className="text-[#6366F1]" />
              </div>
              <h3 className="font-bold text-slate-900 tracking-tight">{f.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#6366F1] flex items-center justify-center">
              <GraduationCap size={17} className="text-white" />
            </div>
            <span className="font-extrabold tracking-tight text-white">Enjyme</span>
            <span className="text-slate-500 text-sm ml-2 font-mono">Career GPS · 2026</span>
          </div>
          <p className="text-slate-500 text-sm">Built for ambitious students. Privacy-first talent discovery.</p>
        </div>
      </footer>
    </div>
  );
}
